import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Diamond as DiamondIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { purple } from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";

const CoinPackagesSettings = () => {
  const { getAllCoinPackagesApiCall } = useAdminContext();
  const [coinPackages, setCoinPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const result = await getAllCoinPackagesApiCall();

      if (result?.coin_packages) {
        setCoinPackages(result.coin_packages);
      }
    } catch (error) {
      console.error("Error loading coin packages:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Coin packages are loaded from the database. These packages are
          available for users to purchase. Manage packages through the database.
        </Alert>

        <Card elevation={3}>
          <CardHeader
            avatar={<DiamondIcon sx={{ color: purple[500] }} />}
            title="Coin Packages"
            subheader="Current coin purchase packages available to users"
            sx={{ bgcolor: purple[50] }}
            action={
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPackages}
                sx={{ color: purple[500], borderColor: purple[500] }}
              >
                Refresh
              </Button>
            }
          />
          <CardContent>
            {coinPackages.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No coin packages found. Add packages to the database.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {coinPackages.map((pkg) => (
                  <Grid item xs={12} sm={6} md={3} key={pkg.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        position: "relative",
                        height: "100%",
                        border: pkg.is_popular
                          ? `2px solid ${purple[500]}`
                          : undefined,
                      }}
                    >
                      {pkg.is_popular ? (
                        <Chip
                          label="POPULAR"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: purple[500],
                            color: "white",
                            fontWeight: "bold",
                            zIndex: 1,
                          }}
                        />
                      ) : null}
                      <CardContent>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {pkg.coins.toLocaleString()}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {" "}
                            coins
                          </Typography>
                        </Typography>

                        {pkg.bonus_coins > 0 && (
                          <Chip
                            label={`+${pkg.bonus_coins} BONUS`}
                            size="small"
                            color="success"
                            sx={{ mb: 2 }}
                          />
                        )}

                        <TextField
                          fullWidth
                          label="Price"
                          value={`$${pkg.price}`}
                          disabled
                          sx={{ mb: 2 }}
                        />

                        {pkg.stripe_price_id && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 2 }}
                          >
                            Stripe ID: {pkg.stripe_price_id}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch checked={pkg.is_popular} disabled />
                            }
                            label="Popular"
                          />
                          <Chip
                            label={pkg.is_active ? "Active" : "Inactive"}
                            size="small"
                            color={pkg.is_active ? "success" : "default"}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CoinPackagesSettings;
